import type { JSONContent } from "@tiptap/react"

import { notebookCanvas } from "./notebookCanvas"
import type { Notebook } from "./notebookSnapshot"

const text = (node: JSONContent): string =>
  node.text ?? node.content?.map(text).join("") ?? ""

/** Disclosures stay interactive in the document; all their content is visible when presenting. */
function expand(nodes: JSONContent[]): JSONContent[] {
  return nodes.flatMap((node) => {
    if (node.type === "details") {
      const summary = node.content?.find(
        (child) => child.type === "detailsSummary",
      )
      const body = node.content?.find(
        (child) => child.type === "detailsContent",
      )
      return [
        {
          type: "heading",
          attrs: { level: 2 },
          content: summary?.content ?? [],
        },
        ...expand(body?.content ?? []),
      ]
    }
    return [
      { ...node, ...(node.content ? { content: expand(node.content) } : {}) },
    ]
  })
}

export function notebookSlides(notebook: Notebook) {
  const nodes = notebookCanvas(notebook).content ?? []
  const labeledSlides = nodes.filter(
    (node) =>
      node.type === "details" &&
      /^slide\s+\d+\b/i.test(text(node.content?.[0] ?? {})),
  )
  const explicitBreaks = nodes.some((node) => node.type === "horizontalRule")
  const groups: { title?: string; content: JSONContent[] }[] = []
  let group: JSONContent[] = []
  let groupTitle: string | undefined
  const flush = () => {
    if (
      groupTitle ||
      group.some(
        (node) =>
          text(node).trim() ||
          !["paragraph", "heading"].includes(node.type ?? ""),
      )
    ) {
      groups.push({ title: groupTitle, content: group })
    }
    group = []
    groupTitle = undefined
  }
  for (const node of nodes) {
    if (labeledSlides.includes(node)) {
      flush()
      groupTitle = text(node.content?.[0] ?? {})
      group = node.content?.[1]?.content ?? []
      flush()
    } else if (node.type === "horizontalRule") {
      flush()
    } else {
      if (
        !explicitBreaks &&
        node.type === "heading" &&
        Number(node.attrs?.level) <= 2
      ) {
        flush()
      }
      group.push(node)
    }
  }
  flush()
  return groups.map(({ title, content }, index) => ({
    title:
      title ||
      text(content.find((node) => node.type === "heading") ?? {}).trim() ||
      `Slide ${index + 1}`,
    content: { type: "doc", content: expand(content) },
  }))
}
